import {round} from '@/helpers';

/**
 * Pure math for the e-sign editor's stacked-page canvas.
 *
 * The canvas renders N pages of `pageHeight` px stacked vertically with a
 * `marginY` px gap between pages, so page i (1-based) occupies
 * [(i - 1) * (pageHeight + marginY), (i - 1) * (pageHeight + marginY) + pageHeight].
 *
 * Note: the previous in-component implementation ignored the accumulated gaps
 * (it tested `pageHeight * (i - 1)` instead of `(pageHeight + marginY) * (i - 1)`),
 * which misattributed the bottom band of page 3+ to the following page.
 */

export function pageNumberForY(
    y: number,
    pageHeight: number,
    marginY: number,
    totalPages: number,
): number {
    const pos = Math.max(0, y);
    const stride = pageHeight + marginY;

    if (totalPages <= 1 || pos < stride) {
        return 1;
    }

    const page = Math.floor(pos / stride) + 1;
    return Math.min(page, totalPages);
}

/**
 * The y position within the page it lands on (gaps removed). A y inside a gap
 * resolves against the page above it, matching pageNumberForY.
 */
export function relativeYForY(
    y: number,
    pageHeight: number,
    marginY: number,
    totalPages: number,
): number {
    const pos = Math.max(0, y);
    const page = pageNumberForY(pos, pageHeight, marginY, totalPages);
    return round(pos - (page - 1) * (pageHeight + marginY), 5);
}

/** Top of a page (1-based) in stacked-canvas coordinates. */
export function pageTopForPage(page: number, pageHeight: number, marginY: number): number {
    return (Math.max(1, page) - 1) * (pageHeight + marginY);
}

/** Bottom of the last page — the lowest y any field may occupy. */
export function stackedCanvasBottom(pageHeight: number, marginY: number, totalPages: number): number {
    if (totalPages <= 0) {
        return 0;
    }
    return totalPages * pageHeight + (totalPages - 1) * marginY;
}
